<!--
 Copyright 2024 Google LLC.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

     https://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
-->

<div class="new-exp-input-fields new-exp-text-style">
  <h2 i18n>Budget Simulation</h2>
  <p i18n *ngIf="!rerun">Create a new A/B experiment starting from the Budget Simulation
    for existing Control and Tests groups.</p>
  <p i18n *ngIf="rerun">Rerun the Budget Simulation for existing Control and Tests groups.</p>
  <h2 i18n>Experiment Details</h2>
  <app-new-experiment-description (nameModified)="storeExperimentName($event)"
    [defaultName]="experimentName" [defaultDescription]="experimentDescription" [isDisabled]="rerun"
    (descriptionModified)="storeExperimentDescription($event)"/>
  <h2>Input File</h2>
  <app-file-picker [inputFileLabel]="'KPI File'" (fileSelected)="storeTestGroupsSelection($event)"
    [matTooltip]="helpMessagesService.newBudgetSimulationPage.FILE_PICKER_KPI"/>
  <app-file-preview [fileToView]="controlTestsGroupedKpiFile" [isChartEnable]="true"/>
  <h2>Test Groups</h2>
  <mat-form-field class="new-exp-form-field">
    <mat-label>Test Groups</mat-label>
    <mat-select #selectTestGroup multiple
      [matTooltip]="helpMessagesService.experimentCommonInputFields.SELECT_TEST_GROUPS">
      <mat-checkbox [(ngModel)]="allSelected" [ngModelOptions]="{standalone: true}" (change)="
        toggleAllSelection()">Select All</mat-checkbox>
      <mat-select-trigger>
        <mat-chip-listbox>
          <mat-chip *ngFor="let selectedTestGroup of getCurrentlySelectedTestGroups()"
            [removable]="true" (removed)="onTestGroupRemoved(selectedTestGroup)">
            {{selectedTestGroup}}
            <mat-icon matChipRemove>cancel</mat-icon>
          </mat-chip>
        </mat-chip-listbox>
      </mat-select-trigger>
      <mat-option *ngFor="let testGroup of testGroupsSelection" [value]="testGroup"
        (click)="updateAllSelected()">
        {{testGroup}}
      </mat-option>
    </mat-select>
  </mat-form-field>
  <h2 i18n>Budget Simulation Parameters</h2>
  <app-numeric-input-field [fieldLabel]="iCPALabel" [minVal]="0.01" [maxVal]="1000000"
    [matTooltip]="helpMessagesService.newBudgetSimulationPage.INPUT_ICPA"
    matTooltipClass="max-width-tooltip"
    [step]="0.01" [defaultValue]="iCPADefaultValue" (fieldModified)="storeICPA($event)">
  </app-numeric-input-field>
  <app-numeric-values-input-field [fieldLabel]="durationsLabel"
    (valuesChanged)="storeDurations($event)"[placeHolderLabel]="durationsPlaceHolder"
    [defaultValues]="durationsDefaultValue" [maxVal]="1000" [maxLength]="8"
    [matTooltip]="helpMessagesService.newBudgetSimulationPage.INPUT_DURATIONS"
    matTooltipClass="multi-line-tooltip max-width-tooltip"/>
  <app-numeric-values-input-field [fieldLabel]="upliftsLabel"
    (valuesChanged)="storeUplifts($event)" [placeHolderLabel]="upliftsPlaceHolder"
    [defaultValues]="upliftsDefaultValue" [minVal]="1.01" [maxVal]="1000" [maxLength]="8"
    [valuesMustBeInteger]="false"
    [matTooltip]="helpMessagesService.newBudgetSimulationPage.INPUT_UPLIFTS"
    matTooltipClass="multi-line-tooltip max-width-tooltip"/>
  <span class="input-btn">
    <button mat-flat-button color="accent" (click)="submitOrRerunBudgetSimulation()"
      [matTooltip]="helpMessagesService.newBudgetSimulationPage.BUTTON_SUBMIT"
      matTooltipClass="max-width-tooltip" i18n>Submit</button>
  </span>
</div>